import { useEffect, useRef } from "react";
import { registerSW } from "virtual:pwa-register";
import { useToast } from "./ToastContext";

// How often to ask the server whether a new build exists.
//
// This poll is the whole point of this component. A browser only re-fetches
// sw.js on navigation, and an installed PWA on Windows or Android can sit open
// for days without ever performing one — so without this it would keep running
// whatever build it launched with, indefinitely.
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

/**
 * Keeps the running app in step with what's deployed.
 *
 * Update policy (owner decision):
 *  - backgrounded  -> apply silently and reload; the user comes back to the
 *                     new build having seen nothing
 *  - on screen     -> offer it, never seize the page. Dealership staff spend
 *                     long stretches inside multi-tab forms, and only the
 *                     add-vehicle flow drafts to localStorage; an unprompted
 *                     reload would throw away the rest.
 *
 * If the prompt is ignored, the pending update is still applied the moment the
 * window loses focus, so "ignored" degrades into "applied quietly" rather than
 * "stuck on the old build".
 *
 * Renders nothing.
 */
export default function PwaUpdater() {
    const { showToast, dismissToast } = useToast();
    // Set once a new build is installed and waiting to take over. Calling it
    // activates the waiting worker and reloads the page.
    const applyUpdate = useRef(null);
    const promptedToastId = useRef(null);

    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;

        let disposed = false;
        let intervalId = null;

        // Takes the pending update. Must be safe to call when there is nothing
        // pending, because the toast that calls it can outlive the update:
        // hiding the window applies silently, and the toast stays on screen.
        //
        // Two failure modes to defend against, both of which previously left
        // the Reload button doing literally nothing:
        //  1. the ref was already consumed by a silent apply, so this returned
        //     early — the common case on desktop, where the window is hidden
        //     and shown many times a day;
        //  2. workbox's messageSkipWaiting is a no-op when registration.waiting
        //     has gone, so no "controlling" event fires and nothing reloads.
        //
        // Either way the user asked to be on the new build, so fall back to a
        // plain reload. That is never worse than doing nothing: the waiting
        // worker takes over on the next navigation regardless.
        const applyNow = () => {
            const apply = applyUpdate.current;
            applyUpdate.current = null; // don't let a double-fire reload twice
            if (promptedToastId.current !== null) {
                dismissToast(promptedToastId.current);
                promptedToastId.current = null;
            }
            const fallback = setTimeout(() => window.location.reload(), 2000);
            try {
                apply?.(true);
            } catch {
                clearTimeout(fallback);
                window.location.reload();
            }
        };

        // When the window goes away with an update pending, take it.
        const onVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                if (applyUpdate.current) applyNow();
                return;
            }
            // Back on screen: a good moment to look for a new build, since the
            // interval may have been throttled while hidden.
            checkForUpdate();
        };

        let registration = null;
        const checkForUpdate = () => {
            // update() on a flaky or offline connection just rejects; skip it
            // rather than emit noise into the console every 15 minutes.
            if (!registration || !navigator.onLine) return;
            registration.update().catch(() => {});
        };

        const updateSW = registerSW({
            immediate: true,

            onRegisteredSW(_swUrl, reg) {
                if (disposed || !reg) return;
                registration = reg;
                intervalId = setInterval(checkForUpdate, CHECK_INTERVAL_MS);
                window.addEventListener("online", checkForUpdate);
                document.addEventListener("visibilitychange", onVisibilityChange);
            },

            onNeedRefresh() {
                if (disposed) return;
                applyUpdate.current = updateSW;

                // Not being watched — just take it.
                if (document.visibilityState === "hidden") {
                    applyNow();
                    return;
                }

                // Being watched — offer it. duration 0 so it waits for them.
                // promptedToastId is cleared when an offer is taken or applied
                // silently, so a later build can offer again rather than being
                // suppressed by a toast that is no longer on screen.
                if (promptedToastId.current !== null) return; // already offered
                promptedToastId.current = showToast(
                    "info",
                    "A new version is available.",
                    0,
                    { label: "Reload", onClick: applyNow },
                );
            },
        });

        return () => {
            disposed = true;
            if (intervalId) clearInterval(intervalId);
            window.removeEventListener("online", checkForUpdate);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            if (promptedToastId.current !== null) dismissToast(promptedToastId.current);
        };
    }, [showToast, dismissToast]);

    return null;
}
